import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { chatService } from '../../services/api';
import { globalChatState } from '../../contexts/ChatStateProvider';
import { PlusIcon, TrashIcon } from 'lucide-react';

const ChatSessions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const lastLoadedSessionRef = React.useRef(null);
  const activeSessionRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);

  useEffect(() => {
    loadSessions();
    
    // Initialize with current state immediately
    const initialState = globalChatState.getSnapshot();
    console.log('[DEBUG-CHAT] 📋 ChatSessions mounted, initial currentSessionId:', initialState.currentSessionId);
    setCurrentSessionId(initialState.currentSessionId);
    
    // Subscribe to chat state changes
    const unsubscribe = globalChatState.subscribe((state) => {
      console.log('[DEBUG-CHAT] 📋 ChatSessions state update, currentSessionId:', state.currentSessionId);
      setCurrentSessionId(state.currentSessionId);
      
      // Update session title in local sessions list when it changes
      if (state.sessionTitle && state.currentSessionId) {
        setSessions(prevSessions => {
          // Safety check: ensure we have sessions to update
          if (!prevSessions || prevSessions.length === 0) {
            console.log('[SESSION-TITLE] ⚠️ No sessions to update yet');
            return prevSessions;
          }
          
          // Check if the session exists in the list
          const sessionExists = prevSessions.some(s => s.session_id === state.currentSessionId);
          if (!sessionExists) {
            console.log('[SESSION-TITLE] ⚠️ Session not in list yet, skipping update');
            return prevSessions;
          }
          
          // Update the session title
          const updatedSessions = prevSessions.map(session => 
            session.session_id === state.currentSessionId
              ? { ...session, session_title: state.sessionTitle }
              : session
          );
          
          console.log('[SESSION-TITLE] ✅ Updated session title in sidebar:', state.sessionTitle);
          return updatedSessions;
        });
      }
    });
    return unsubscribe;
  }, []);

  // Reload sessions when a new session is created (not in current list)
  // Reload sessions when a new session is created (not in current list)
  useEffect(() => {
    if (currentSessionId && 
        currentSessionId !== lastLoadedSessionRef.current && 
        !sessions.find(s => s.session_id === currentSessionId)) {
      console.log('[DEBUG-CHAT] New session detected, reloading sessions list');
      lastLoadedSessionRef.current = currentSessionId;
      loadSessions();
    }
  }, [currentSessionId]);

  // Scroll active session into view when it changes
  useEffect(() => {
    if (activeSessionRef.current && scrollContainerRef.current) {
      activeSessionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [currentSessionId]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await chatService.getChatSessions();
      setSessions(response.sessions || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = async () => {
    console.log('[DEBUG-CHAT] New chat button clicked - clearing session and navigating to /chat');
    await globalChatState.clearSession();
    navigate('/chat');
  };

  const selectSession = async (sessionId) => {
    try {
      await globalChatState.switchSession(sessionId);
      navigate(`${location.pathname}?session=${sessionId}`);
    } catch (error) {
      console.error('Failed to switch session:', error);
    }
  };

  const deleteSession = async (sessionId, event) => {
    event.stopPropagation(); // Prevent triggering selectSession
    try {
      await chatService.deleteChatSession(sessionId);
      // Remove from local state instead of full refresh
      setSessions(prev => prev.filter(session => session.session_id !== sessionId));
      
      // If we're currently viewing the deleted session, navigate to chat root
      if (currentSessionId === sessionId) {
        await globalChatState.clearSession();
        navigate('/chat');
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  return (
    <div className="ml-8 mt-1 space-y-1">
      {/* New Chat Button */}
      <button
        onClick={handleNewChat}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md transition-colors"
      >
        <PlusIcon className="w-3 h-3" />
        New chat
      </button>

      {/* Sessions List */}
      {loading ? (
        <div className="space-y-1">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-6 bg-slate-100 dark:bg-slate-800 rounded-md animate-pulse" />
          ))}
        </div>
      ) : (
        <div 
          ref={scrollContainerRef}
          className="space-y-0.5 max-h-[240px] overflow-y-auto overflow-x-hidden pr-3 custom-scrollbar"
        >
          {sessions.map((session) => (
            <div 
              key={session.session_id} 
              className="group relative"
              ref={session.session_id === currentSessionId ? activeSessionRef : null}
            >
              <div className="flex items-center group/session">
                <button
                  onClick={() => selectSession(session.session_id)}
                  title={session.session_title || 'New conversation'}
                  className={`flex-1 text-left px-3 py-1.5 rounded-md text-xs transition-all duration-200 truncate transform ${
                    session.session_id === currentSessionId
                      ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white scale-[1.02]'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:scale-[1.01] hover:translate-x-1'
                  }`}
                  data-session-id={session.session_id}
                  data-current-id={currentSessionId}
                  data-is-active={session.session_id === currentSessionId}
                >
                  {session.session_title || 'New conversation'}
                </button>
                
                {/* Delete button */}
                <button
                  onClick={(e) => deleteSession(session.session_id, e)}
                  className="opacity-0 group-hover/session:opacity-100 p-1 ml-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                  title="Delete conversation"
                >
                  <TrashIcon size={12} />
                </button>
              </div>
            </div>
          ))}
          
          {sessions.length === 0 && !loading && (
            <div className="px-3 py-1.5 text-xs text-slate-400 dark:text-slate-500">
              No conversations yet
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatSessions;